import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Real, committed Terms of Service content -- rendered whenever there's no
// (or an unreachable) Sharetribe Console-hosted asset for this page, which
// in practice has always been the case in this environment, so this is
// effectively the live page content, not just an error fallback.
const fallbackTerms = `
*Last updated: August 2026*

*This document was drafted to accurately describe Coachify as built, following common practice used by other marketplace platforms. It is not a substitute for legal advice -- have an attorney review it, especially the liability, release, and dispute-resolution sections, before relying on it at meaningful transaction volume.*

## 1. What Coachify is

Coachify Marketplace (coachifyusa.com) is a platform that connects independent coaches and businesses ("**Providers**") with people looking to book their services ("**Customers**"). **Coachify is not a party to the coaching relationship.** Providers are independent third parties, not Coachify employees, contractors, or agents. Coachify does not supervise, direct, or control how a Provider delivers a session, and does not guarantee the quality, safety, legality, or outcome of any session booked through the platform.

Coachify Maps (discovery.coachifyusa.com and the Coachify mobile app) is a separate, free discovery layer for finding Providers nearby. It does not process bookings or payments.

By creating an account or using Coachify, you agree to these Terms.

## 2. Eligibility and your account

You must be at least 18 years old and able to form a binding contract to create an account. You are responsible for complying with all laws that apply to you in your location, including any age-of-consent, licensing, or activity-specific regulations relevant to the coaching services you seek or provide.

**You are responsible for representing yourself accurately.** Any information you provide -- your identity, qualifications, certifications, business details, or anything on your profile -- must be truthful and current. Coachify does not independently verify every credential or qualification a Provider lists, and does not warrant the accuracy of any profile beyond the review tools available on the platform (verification badges, ratings, and reports). Booking or providing services through a false identity or false credentials is a violation of these Terms and may result in immediate account termination.

You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.

## 3. Bookings and payments

A booking is a direct agreement between a Customer and a Provider. Coachify facilitates that agreement by providing scheduling, messaging, and payment processing tools, but is not a party to it. Payments are processed through Stripe. Coachify charges a platform booking fee (currently 8% of the session price) on completed bookings, disclosed at checkout.

**Coachify is not responsible for a Provider's failure to show up, perform, or deliver a session as described, nor for a Customer's conduct toward a Provider.** We provide cancellation, refund, reporting, and dispute tools as a courtesy to help resolve issues between users -- see our [Cancellation & Refund Policy](/cancellation-policy) -- but we do not guarantee any particular outcome and are not obligated to resolve a dispute between a Customer and a Provider in either party's favor.

## 4. Consent and assumption of risk

By booking, accepting, or attending a session on Coachify, you acknowledge and agree that:

- You are voluntarily choosing to receive or provide coaching services from an independent third party you selected.
- Coaching and athletic activity carry inherent risks, and you are solely responsible for assessing your own fitness, health, and ability to safely participate, and for following any guidance a medical professional has given you.
- You consent to sharing the information reasonably necessary to complete a booking -- your name, contact details, and (for in-person sessions) a meeting location -- with the other party to that booking.
- Coachify does not provide medical advice, and nothing on the platform should be treated as such.

**You, not Coachify, are responsible for your own safety decisions and for the personal information you choose to share with another user.**

## 5. Content and conduct

You're responsible for anything you post -- profile content, reviews, messages, photos. You may not post anything illegal, harassing, fraudulent, or that infringes someone else's rights. We provide in-app tools to report content or block another user, and we may remove content, issue warnings, or suspend or terminate accounts that violate these Terms, at our discretion.

## 6. Disclaimer of warranties

Coachify is provided "as is" and "as available," without warranties of any kind, whether express or implied, including any implied warranty of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the platform will be uninterrupted, error-free, or secure, or that any Provider will meet your expectations.

## 7. Limitation of liability

To the fullest extent permitted by law, Coachify and its officers, employees, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or goodwill, arising from your use of the platform, a booking, or the acts or omissions of any other user -- including personal injury, property damage, or any dispute between a Customer and a Provider. **Coachify's total liability to you for any claim arising from these Terms or your use of the platform will not exceed the greater of $100 or the amount of platform fees you paid to Coachify in the 12 months before the claim arose.**

Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above limitations may not apply to you.

## 8. Release and indemnification

**You release Coachify from any claims, demands, or damages arising out of a dispute with another user** -- including a dispute over a session's quality, a Provider's or Customer's conduct, a cancellation, or a payment -- to the fullest extent permitted by law. Coachify is a platform connecting independent users; it is not responsible for the wrongdoing of one user toward another.

You agree to indemnify and hold Coachify harmless from any claim or demand, including reasonable attorneys' fees, made by a third party arising out of: your breach of these Terms, your violation of any law, or your misuse of the platform.

## 9. Disputes between users

Coachify offers reporting, blocking, and cancellation-request tools to help users resolve issues directly. We are not a mediator or arbitrator between Customers and Providers and have no obligation to resolve a dispute in a particular way, though we may take account-level action (warnings, holds, suspension) where our own policies have been violated.

## 10. Termination

You may stop using Coachify at any time by deleting your account. We may suspend or terminate your access to Coachify, with or without notice, for violating these Terms, for fraudulent or harmful activity, or as required by law.

## 11. Governing law and disputes with Coachify

Any dispute between you and Coachify arising from these Terms will first be addressed through good-faith informal negotiation by contacting support@coachifyusa.com. These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles, except where local consumer-protection law requires otherwise.

## 12. Changes to these terms

We may update these Terms from time to time. Continued use of Coachify after a change takes effect constitutes acceptance of the updated Terms.

## 13. Contact

support@coachifyusa.com, or visit coachifyusa.com/support.
`;

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms of Service' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackTerms,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Terms of service page',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Terms of service fetch failed',
    },
  },
};

// This is the fallback page, in case there's no Terms of Service asset defined in Console.
const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
